// The order's drawer: who, where it stands on its journey, the rider, the
// bottle and the money, and the journal of every step. Phase 3: a MyGaz admin
// assigns, changes rider and cancels from here (OrderActions, migration 0045).
import { useEffect, useRef, useState } from 'react';
import { S } from '../lib/strings';
import { telHref, waHref } from '../lib/phone';
import { dec1, discColor, hhmm, initials, minutesBetween, xaf } from '../lib/format';
import { amount, isCancelled, isDone, stepIdx, steps, stepTimes } from '../lib/orders';
import type { OpsOrder } from '../lib/types';
import { BrandCyl } from './Bottles';
import { Ic } from './Icon';
import { StPill } from '../views/Orders';
import type { Source } from '../lib/api';
import { type ActMode, OrderActions, assignLabel, canAssign, canCancel } from './OrderActions';

const EVC: Record<string, string> = { placed: 'violet', assigned: 'blue', agent_en_route: 'blue', tech_arrived: 'blue', install_in_progress: 'blue', install_completed: 'green', delivered: 'green', completed: 'green', failed_first_install: 'red', cancelled_by_customer: '', cancelled_by_agency: '' };

function Head({ o }: { o: OpsOrder }) {
  const done = isDone(o.status), times = stepTimes(o), last = times[times.length - 1];
  if (o.status === 'agent_en_route' && o.eta_at) {
    const m = Math.max(1, Math.round((new Date(o.eta_at).getTime() - Date.now()) / 60000));
    return <><b className="num">{m} min</b><span>{o.distance_m ? dec1(o.distance_m / 1000) + ' km · ' : ''}{S.dr.toward(hhmm(o.eta_at))}</span></>;
  }
  if (o.status === 'placed') return <><b style={{ color: 'var(--violet-ink)' }}>{S.dr.toAssign}</b><span>{S.dr.placedAgo(Math.max(1, Math.round(minutesBetween(o.created_at))))}</span></>;
  if (o.status === 'tech_arrived') return <><b>{S.dr.atDoor}</b><span>{S.dr.atDoorSub}</span></>;
  if (o.status === 'install_in_progress') return <><b>{S.dr.inProgress}</b><span>{S.dr.since(Math.round(minutesBetween(o.events.install_in_progress ?? o.created_at)))}</span></>;
  if (done && last) return <><b style={{ color: 'var(--green-ink)' }}>{S.dr.done(o.order_type === 'first_install')}</b><span>{S.dr.doneSub(hhmm(last), Math.round(minutesBetween(o.created_at, last)))}</span></>;
  if (o.status === 'failed_first_install') return <><b style={{ color: 'var(--red-ink)' }}>{S.dr.failed}</b><span>{o.events.failed_first_install ? hhmm(o.events.failed_first_install) : ''}</span></>;
  if (isCancelled(o.status)) return <b className="muted">{S.dr.cancelled}</b>;
  return <b>{S.st[o.status]}</b>;
}

export function OrderDrawer({ order, onClose, onHome, src, onChanged }: { order: OpsOrder | null; onClose(): void; onHome?(id: string): void; src?: Source; onChanged?(): void }) {
  const ref = useRef<HTMLElement>(null);
  const open = !!order;
  const [mode, setMode] = useState<ActMode>(null);
  useEffect(() => setMode(null), [order?.id]);
  useEffect(() => { if (open) setTimeout(() => ref.current?.focus({ preventScroll: true }), 50); }, [open]);
  const o = order;
  return (
    <>
      <div className={'scrim' + (open ? ' on' : '')} onClick={onClose} />
      <aside ref={ref} className={'drawer' + (open ? ' on' : '')} aria-hidden={!open} aria-label={S.tabs.ord} tabIndex={-1}>
        {o && (() => {
          const ST = steps(o), c = stepIdx(o), done = isDone(o.status), bad = o.status === 'failed_first_install', canc = isCancelled(o.status);
          const times = stepTimes(o), amt = amount(o), inst = o.order_type === 'first_install';
          return (
            <>
              <div className="dh"><span className="mono">{o.ref}</span><StPill o={o} /><span style={{ flex: 1 }} />
                <button className="xbtn" aria-label={S.dr.close} onClick={onClose}><Ic n="x" s="s" /></button></div>
              <div className="db">
                <div className="hero">
                  <div><h2>{o.customer.name}</h2><p>{o.customer.quartier} · {o.agency.name} · {inst ? S.dr.install : o.order_type === 'replacement' ? S.dr.replacement : S.dr.refill}</p></div>
                  <div className="eta"><Head o={o} /></div>
                </div>
                <div className="jy" style={{ ['--n' as string]: ST.length }} role="list" aria-label={S.dr.parcours}>
                  {ST.map((s, x) => {
                    let cl = done ? 'ok' : x < c ? 'd' : x === c ? (bad || canc ? 'x' : 'c') : 'u';
                    if (bad && x === c) cl = 'x';
                    const t = times[x], prev = x ? times[x - 1] : null;
                    return (
                      <div key={s.key} className={'js ' + cl} role="listitem">
                        {t && prev && <span className="dur">{Math.max(1, Math.round(minutesBetween(prev, t)))} min</span>}
                        <span className="jc"><Ic n={cl === 'x' ? 'x' : (s.icon as 'doc')} s="s" /></span>
                        <b>{cl === 'x' && bad ? S.steps.failed : s.label}</b>
                        <span className="num">{t ? hhmm(t) : x === c + 1 && o.status === 'agent_en_route' && o.eta_at ? S.dr.toward(hhmm(o.eta_at)) : '—'}</span>
                      </div>
                    );
                  })}
                </div>
                {bad && o.failure_note && (
                  <div className="issue red"><Ic n="alert" /><div><b>{S.dr.failReason}</b><p>{o.failure_note}</p></div></div>
                )}
                <div className="pair">
                  <div className="box"><span className="cap">{inst ? S.dr.tech : S.dr.rider}</span>
                    {o.rider ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span className="ini lg" style={{ background: discColor(o.rider.id) }}>{initials(o.rider.name)}</span>
                          <div style={{ flex: 1, minWidth: 0 }}><b style={{ fontSize: 14 }}>{o.rider.name}</b>
                            <div className="muted" style={{ fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.rider.rating ? '★ ' + dec1(o.rider.rating) + ' · ' : ''}{o.rider.plate ?? ''}</div></div>
                          {o.rider.phone && <a className="ibtn" href={telHref(o.rider.phone)} aria-label={o.rider.name}><Ic n="phone" s="s" /></a>}
                        </div>
                        <div className="row t" style={{ marginTop: 12 }}><span style={{ color: 'var(--ink)' }}>{S.dr.agency}</span><span>{o.agency.name}</span></div>
                      </>
                    ) : <div className="muted" style={{ fontSize: 13 }}>{o.status === 'placed' ? S.dr.nobody : '—'}</div>}
                  </div>
                  <div className="box"><span className="cap">{S.dr.bottle}</span>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 6 }}>
                      {o.marketer && <BrandCyl color={o.marketer.brand_color} h={36} />}<b style={{ fontSize: 14 }}>{o.marketer?.name ?? '—'} · {dec1(o.size_kg)} kg</b></div>
                    {o.kit && <div className="row"><span>{S.dr.kitMomo}</span><b className="num">{xaf(o.kit.price)}</b></div>}
                    {o.gas_price != null && <div className="row"><span>{S.dr.gas}</span><span className="num" style={{ color: 'var(--ink)' }}>{xaf(o.gas_price)}</span></div>}
                    <div className="row"><span>{S.dr.delivery}</span><span className="num" style={{ color: 'var(--ink)' }}>{xaf(o.delivery_fee ?? o.fare)}</span></div>
                    <div className="row t"><span style={{ color: 'var(--ink)' }}>{S.dr.cashToRider}</span><span className="num">{xaf(amt.cash)}</span></div>
                  </div>
                </div>
                <div className="log"><h3>{S.dr.log}</h3>
                  {o.log.slice().sort((a, b) => b.at.localeCompare(a.at)).map((e, i) => (
                    <div key={i} className={'ev ' + (EVC[e.status] ?? '')}>
                      <time>{hhmm(e.at)}</time><span className="evd" />
                      <div><b>{S.dr.ev[e.status]}</b><span className="who2">· {S.dr.actor[e.actor]}</span></div>
                    </div>
                  ))}
                </div>
                {src && <OrderActions o={o} mode={mode} setMode={setMode} src={src} onDone={() => onChanged?.()} />}
              </div>
              <div className="df">
                {o.customer.phone && <a className="btn sm" href={telHref(o.customer.phone)}><Ic n="phone" s="xs" />{S.dr.callCustomer}</a>}
                {o.customer.phone && <a className="ibtn" href={waHref(o.customer.phone)} target="_blank" rel="noreferrer" aria-label={S.wa} title={S.waHint}><Ic n="msg" s="s" /></a>}
                {onHome && <button className="btn sm" onClick={() => { onClose(); onHome(o.customer_id); }}>{S.dr.seeHome}</button>}
                <span className="sp" />
                {src && canCancel(o) && mode !== 'cancel' && <button className="btn sm dline" onClick={() => setMode('cancel')}>{S.act.cancelShort}</button>}
                {src && canAssign(o) && mode !== 'assign' && <button className="btn sm pri" onClick={() => setMode('assign')}>{assignLabel(o)}</button>}
              </div>
            </>
          );
        })()}
      </aside>
    </>
  );
}
